<template>
  <b-container class="p-4">
    <b-form @submit.prevent="saveFunction">
      <b-row class="form pt-2">
        <span id="top-function"></span>

        <b-col sm="8">
          <b-form-group :label="FormMSG(200, 'Prefix:')" label-for="prefix" :label-cols="3">
            <b-form-input v-model="currentFunction.prefix" type="text" id="prefix" />
          </b-form-group>

          <b-form-group :label="FormMSG(2, 'Function:')" label-for="funName" :label-cols="3">
            <b-form-input v-model="currentFunction.message" type="text" id="funName" />
          </b-form-group>

          <b-form-group :label="FormMSG(24, 'Short code')" label-for="shortCode" :label-cols="3">
            <b-form-input v-model="currentFunction.shortCode" type="text" id="shortCode" />
          </b-form-group>

          <b-form-group :label="FormMSG(100, 'Category')" label-for="category" :label-cols="3">
            <treeselect
              v-model="currentFunction.categoryLinkedToFun"
              :options="categories"
              :flat="true"
              :disable-branch-nodes="false"
              :multiple="false"
              id="category"
            />
          </b-form-group>

          <b-form-group :label="FormMSG(9, 'Reference')" label-for="reference" :label-cols="3">
            <b-form-input v-model="currentFunction.costCenter" type="text" id="reference" disabled />
          </b-form-group>
        </b-col>

        <b-col sm="4">
          <div class="w-100" :class="isMobile ? 'footer-fixed' : 'pull-right'" :style="`left: ${isMobile ? '0' : '15'}px;`">
            <b-button @click="saveFunction" variant="primary" :disabled="isSaving" size="md" block class="mb-2">
              <b-spinner v-if="isSaving" small class="mr-2"></b-spinner>
              {{ FormMSG(4, 'Save') }}
            </b-button>
          </div>
        </b-col>
      </b-row>
    </b-form>

    <b-row class="mt-3">
      <b-col sm="4"></b-col>
      <b-col sm="4">
        <b-button @click="createNewFunction" variant="primary" size="md" block :disabled="isCreating">
          <b-spinner v-if="isCreating" small class="mr-2"></b-spinner>
          {{ FormMSG(5, 'Create a new function') }}
        </b-button>
      </b-col>
      <b-col sm="4"></b-col>
    </b-row>

    <b-table
      v-if="showTable"
      v-show="functions.length > 0"
      :items="functions"
      :fields="tableFields"
      :hover="true"
      :current-page="currentPage"
      :per-page="perPage"
      :select-mode="'single'"
      :selectedVariant="'primary'"
      :head-variant="'dark'"
      class="mt-4"
      responsive="sm"
      ref="functionsTable"
      selectable
      bordered
      small
      sticky-header="700px"
      style="text-align: left"
      @row-clicked="selectFunction"
    >
      <template v-slot:cell(rem)="{ item }">
        <b-button variant="outline-danger" size="sm" @click="confirmDeleteFunction(item.id)" :disabled="isDeleting">
          <component :is="getLucideIcon('Trash2')" :size="16" />
        </b-button>
      </template>
    </b-table>

    <!-- Empty State -->
    <b-alert v-if="functions.length === 0 && !isLoading" show variant="info" class="mt-4 text-center">
      <h5>{{ FormMSG(300, 'No functions found') }}</h5>
      <p>{{ FormMSG(301, 'Create your first function using the button above.') }}</p>
    </b-alert>

    <!-- Loading State -->
    <div v-if="isLoading" class="text-center mt-4">
      <b-spinner variant="primary"></b-spinner>
      <p class="mt-2">{{ FormMSG(302, 'Loading functions...') }}</p>
    </div>
  </b-container>
</template>

<script>
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import { getBudgetHierarchicalCategoryJson } from '../../cruds/budget.crud';
import { removeAttributeTree } from '../../shared/utils';
import globalMixin from '@/mixins/global.mixin';
import { getFunctions, updateFunction, deleteFunction, addFunction } from '@/cruds/department.crud';

// Constants
const EMPTY_FUNCTION = {
  value: 0,
  id: null,
  prefix: '',
  message: '',
  shortCode: '',
  categoryLinkedToFun: null,
  costCenter: ''
};

const MOBILE_BREAKPOINT = 576;
const DESKTOP_BREAKPOINT = 992;

export default {
  name: 'FunctionForm',

  components: {
    Treeselect
  },

  mixins: [globalMixin],

  props: {
    department: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      // Current function being edited
      currentFunction: { ...EMPTY_FUNCTION },

      // Data collections
      functions: [],
      categories: [],

      // UI state
      isLoading: false,
      isSaving: false,
      isCreating: false,
      isDeleting: false,

      // Table configuration
      currentPage: 1,
      perPage: 10,

      // Table field definitions
      tableFields: [
        {
          key: 'costCenter',
          label: this.FormMSG(8, 'Reference'),
          sortable: true
        },
        {
          key: 'prefix',
          label: this.FormMSG(201, 'Prefix'),
          sortable: true
        },
        {
          key: 'message',
          label: this.FormMSG(21, 'Function name'),
          sortable: true
        },
        {
          key: 'shortCode',
          label: this.FormMSG(25, 'Short code'),
          sortable: true
        },
        {
          key: 'rem',
          label: this.FormMSG(22, 'Remove'),
          sortable: false,
          class: 'text-center'
        }
      ]
    };
  },

  computed: {
    // Computed properties for better readability
    departmentId() {
      return parseInt(this.department.value, 10);
    },

    isMobile() {
      return this.$screen.width <= MOBILE_BREAKPOINT;
    },

    showTable() {
      return this.$screen.width >= DESKTOP_BREAKPOINT;
    },

    // Legacy computed properties for backward compatibility
    Funcs() {
      return this.functions;
    },

    Cats() {
      return this.categories;
    }
  },

  methods: {
    /**
     * Initialize component data
     */
    async init() {
      this.isLoading = true;
      try {
        await this.loadFunctionsAndCategories();
      } catch (error) {
        console.error('Error initializing component:', error);
        this.showErrorToast('Error loading data');
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Load functions and categories data
     */
    async loadFunctionsAndCategories() {
      await Promise.all([this.loadFunctions(), this.loadCategories()]);
    },

    /**
     * Load functions for the current department
     */
    async loadFunctions() {
      try {
        this.functions = await getFunctions(this.departmentId);
        this.selectLatestFunction();
      } catch (error) {
        console.error('Error loading functions:', error);
        throw error;
      }
    },

    /**
     * Load hierarchical categories
     */
    async loadCategories() {
      try {
        const categoriesResponse = await getBudgetHierarchicalCategoryJson(-2);
        const processedCategories = this.processCategories(categoriesResponse);

        this.categories = [
          {
            id: -1,
            label: this.FormMSG(76, 'Select a category')
          },
          ...processedCategories
        ];
      } catch (error) {
        console.error('Error loading categories:', error);
        throw error;
      }
    },

    /**
     * Process categories tree structure
     */
    processCategories(categoriesTree) {
      const updateTree = (tree) => {
        return tree
          .map((node) => {
            const newNode = { ...node };

            // Disable nodes based on parent department
            newNode.isDisabled = newNode.data.parentDepartment > 0;

            // Process children recursively
            if (newNode.data.category !== 0) {
              if (newNode.children && newNode.children.length > 0) {
                newNode.children = updateTree(newNode.children);
              }
              return newNode;
            }
          })
          .filter((node) => node !== undefined);
      };

      const cleanedTree = removeAttributeTree(categoriesTree, 'children', null);
      return updateTree(cleanedTree);
    },

    /**
     * Select the latest function or set default values
     */
    selectLatestFunction() {
      if (this.functions.length > 0) {
        const latestFunction = this.functions.reduce((max, fn) => (fn.id > max.id ? fn : max), this.functions[0]);

        this.currentFunction = {
          ...latestFunction
        };
      } else {
        this.currentFunction = {
          ...EMPTY_FUNCTION,
          departmentNumber: this.departmentId
        };
      }
    },

    /**
     * Handle function row click in table
     */
    selectFunction(selectedFunction) {
      this.currentFunction = { ...selectedFunction };
      this.scrollToTop();
    },

    /**
     * Scroll to top of form
     */
    scrollToTop() {
      this.$nextTick(() => {
        const element = document.getElementById('top-function');
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'end'
          });
        }
      });
    },

    /**
     * Save current function
     */
    async saveFunction() {
      if (!this.validateFunction()) {
        return;
      }

      this.isSaving = true;

      try {
        const functionData = this.prepareFunctionData();
        const result = await updateFunction(...functionData);

        if (result) {
          this.$emit('function-form:function-updated', result);
          await this.handleSaveSuccess();
        }
      } catch (error) {
        console.error('Error saving function:', error);
        this.showErrorToast('Error saving function');
      } finally {
        this.isSaving = false;
      }
    },

    /**
     * Validate current function data
     */
    validateFunction() {
      const requiredFields = ['message'];
      const missingFields = requiredFields.filter((field) => !this.currentFunction[field] || this.currentFunction[field].trim() === '');

      if (missingFields.length > 0) {
        this.showWarningToast(`Please fill in: ${missingFields.join(', ')}`);
        return false;
      }

      return true;
    },

    /**
     * Prepare function data for API call
     */
    prepareFunctionData() {
      const category = this.currentFunction.categoryLinkedToFun || -1;

      return [
        parseInt(this.currentFunction.id, 10),
        this.currentFunction.message,
        this.currentFunction.costCenter,
        this.currentFunction.shortCode,
        this.currentFunction.prefix,
        category
      ];
    },

    /**
     * Handle successful function save
     */
    async handleSaveSuccess() {
      await this.loadFunctions();
      this.resetForm();
      this.showSuccessToast(this.FormMSG(155, 'Success'), this.FormMSG(165, 'Function updated successfully'));
    },

    /**
     * Create a new function
     */
    async createNewFunction() {
      this.isCreating = true;

      try {
        const result = await addFunction(this.departmentId);

        if (result) {
          this.resetForm();
          await this.loadFunctions();
          this.showSuccessToast(this.FormMSG(155, 'Success'), this.FormMSG(304, 'New function created successfully'));
        }
      } catch (error) {
        console.error('Error creating function:', error);
        this.showErrorToast('Error creating new function');
      } finally {
        this.isCreating = false;
      }
    },

    /**
     * Confirm function deletion
     */
    confirmDeleteFunction(functionId) {
      const confirmationText = this.FormMSG(1594, 'Are you sure you want to delete this function?');

      this.$bvModal
        .msgBoxConfirm(confirmationText, {
          title: this.FormMSG(23, 'Confirm'),
          size: 'md',
          buttonSize: 'md',
          okVariant: 'danger',
          cancelVariant: 'secondary',
          okTitle: this.FormMSG(1596, 'Delete'),
          cancelTitle: this.FormMSG(1595, 'Cancel'),
          noCloseOnBackdrop: true,
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        })
        .then(async (confirmed) => {
          if (confirmed) {
            await this.deleteFunction(functionId);
          }
        })
        .catch((error) => {
          console.error('Error in confirmation dialog:', error);
        });
    },

    /**
     * Delete function
     */
    async deleteFunction(functionId) {
      this.isDeleting = true;

      try {
        const result = await deleteFunction(parseInt(functionId, 10), true);

        if (result) {
          await this.handleDeleteSuccess();
        }
      } catch (error) {
        console.error('Error deleting function:', error);
        this.showErrorToast('Error deleting function');
      } finally {
        this.isDeleting = false;
      }
    },

    /**
     * Handle successful function deletion
     */
    async handleDeleteSuccess() {
      await this.loadFunctions();
      this.showSuccessToast(this.FormMSG(155, 'Success'), this.FormMSG(158, 'Function deleted successfully'));
    },

    /**
     * Reset form to empty state
     */
    resetForm() {
      this.currentFunction = { ...EMPTY_FUNCTION };
    },

    /**
     * Toast notification helpers
     */
    showSuccessToast(title, message) {
      this.createToastForMobile(title, message, '', 'success');
    },

    showErrorToast(message) {
      this.createToastForMobile('Error', message, '', 'danger');
    },

    showWarningToast(message) {
      this.createToastForMobile('Warning', message, '', 'warning');
    },

    /**
     * Legacy method names for backward compatibility
     */
    async reloadData() {
      await this.loadFunctionsAndCategories();
    },

    rowClicked(item) {
      this.selectFunction(item);
    },

    async curFunUpd() {
      await this.saveFunction();
    },

    newFunction() {
      return this.createNewFunction();
    },

    delItem(id) {
      this.confirmDeleteFunction(id);
    }
  },

  async mounted() {
    await this.init();
  }
};
</script>
